import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useModalClose from "../../../hooks/useModalClose";
import closeBtn from "../../../assets/icons/close.png";
import NonLGVInventoryTable from "./NonLGVInventoryTable";
import "../../../assets/css/NonLgvModal/nonLgvModalStyles.css";
import { setIsNonLgvModalOpen } from "../../../store/plantSlice";
import LocationList from "./LocationList";
import { isFiltersEnabled, matches } from "../../FilterDrawer/utils";
import ExportButton from "../../Reusables/ExportButton/ExportButton";
import { Button, Flex } from "antd";

// convert the name from "C.E.001" to "C.E", "TAC201" to "TAC201"
const transformLocationName = (data) =>
  Object.groupBy(data, ({ location }) =>
    location.split(".").length > 1
      ? location.split(".").slice(0, 2).join(".")
      : location
  );

const MODAL_ID = "lgv-wrapper-id";

const NonLgvModal = () => {
  const dispatch = useDispatch();
  const nonLgvData = useSelector((state) => state.plant.nonLGVInventory.data);
  const { results: filterResults } = useSelector((state) => state.inventory);
  const {
    location: selectedLocation,
    sku: selectedSKU,
    lpn: selectedLPN,
    qualityStatus: selectedQualityStatus,
  } = useSelector((state) => state.inventory.filters);

  const [nonLgvSocketData, setNonLgvSocketData] = useState({});
  const [selectedBlockKey, setSelectedBlockKey] = useState(null);
  const [selectedInventoryData, setSelectedInventoryData] = useState(null);
  const [exportData, setExportData] = useState([]);
  const [showClearFilter, setShowClearFilter] = useState(false);
  const [clearFiltersFn, setClearFiltersFn] = useState(null);
  const updateNonLgvExportData = useCallback((data) => {
    setExportData(data);
  }, []);

  useEffect(() => {
    document.body.classList.add("prevent-scroll");

    const hasNonLGVItem = filterResults.some(
      (item) => item.storageType === "nonLGV"
    );

    if (!hasNonLGVItem) setNonLgvSocketData(transformLocationName(nonLgvData));
    else {
      if (isFiltersEnabled()) {
        const filteredItems = nonLgvData.filter((item) =>
          matches(item, {
            selectedLocation,
            selectedLPN,
            selectedQualityStatus,
            selectedSKU,
          })
        );
        setNonLgvSocketData(transformLocationName(filteredItems));
      }
    }
  }, [
    nonLgvData,
    filterResults,
    selectedLocation,
    selectedLPN,
    selectedQualityStatus,
    selectedSKU,
  ]);

  useEffect(() => {
    if (Object.keys(nonLgvSocketData).length === 0) return;

    const hasDataForSelected =
      selectedBlockKey !== null && nonLgvSocketData[selectedBlockKey];

    if (hasDataForSelected) {
      //retaining data selected location when new data flows
      setSelectedInventoryData(nonLgvSocketData[selectedBlockKey]);
    } else {
      setSelectedBlockKey(Object.keys(nonLgvSocketData)[0]);
      setSelectedInventoryData(
        nonLgvSocketData[Object.keys(nonLgvSocketData)[0]]
      );
    }
  }, [nonLgvSocketData]);

  const closeModalHandler = () => {
    dispatch(setIsNonLgvModalOpen(false));
    document.body.classList.remove("prevent-scroll");
  };

  const lgvListItemHandler = useCallback((location, data) => {
    setSelectedBlockKey(location);
    setSelectedInventoryData(data);
  }, []);

  const dataEntries = useMemo(
    () => Object.entries(nonLgvSocketData),
    [nonLgvSocketData]
  );

  useModalClose(MODAL_ID, closeModalHandler);

  const handleClearFilter = () => clearFiltersFn && clearFiltersFn();

  return (
    <div id={MODAL_ID} className="popup-wrapper">
      <div className="popup-container">
        <div className="popup-header">
          <div className="popup-title">Non LGV Inventories</div>
          <div className="popup-close">
            <div
              onClick={closeModalHandler}
              className="close-btn"
              id="close-btn"
            >
              <img src={closeBtn} alt="close button" />
            </div>
          </div>
        </div>
        {nonLgvData && nonLgvData.length !== 0 ? (
          <Flex vertical gap="small" style={{ height: "90%" }}>
            <Flex justify="flex-end" gap="small" style={{ width: "100%" }}>
              {showClearFilter && (
                <Button
                  color="danger"
                  variant="outlined"
                  style={{
                    padding: "20px",
                    borderRadius: "20px",
                  }}
                  onClick={handleClearFilter}
                >
                  Clear Filters
                </Button>
              )}
              <ExportButton data={exportData} type={selectedBlockKey} />
            </Flex>
            <div className="popup-bottom">
              <div className="left-selector-section">
                <LocationList
                  dataEntries={dataEntries}
                  selectedBlockKey={selectedBlockKey}
                  lgvListItemHandler={lgvListItemHandler}
                />
              </div>
              <div className="vertical-scroller">
                {selectedInventoryData && (
                  <NonLGVInventoryTable
                    selectedInventoryData={selectedInventoryData}
                    updateNonLgvExportData={updateNonLgvExportData}
                    setClearFiltersFn={setClearFiltersFn}
                    setShowClearFilter={setShowClearFilter}
                  />
                )}
              </div>
            </div>
          </Flex>
        ) : (
          <div className="popup-bottom flex-center">
            <div className="non-lgv-table-loader">
              <h4>No data available!</h4>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(NonLgvModal);
